import { PERMISSIONS } from 'interfaces/user';
import { get } from 'lodash/fp';
import apiUtils from 'utils/api';
import { sendAuthenticatedRequest } from 'utils/auth/authenticatedRequest';
import sessionCoordinator from 'utils/auth/sessionCoordinator';
import { getHttpStatusFromError, isAuthenticationError } from './httpError';
import { hasPermissionLegacy } from './user';

interface Params<T = any, E = any> {
  url: string;
  onSuccess(body: T): void;
  onFailure(error: E): void;
  data?: any;
  method: string;
  signal?: AbortSignal;
  mediaType?: string;
  responseType?: string;
}

export const apiRequestHandler = <T = any, E = any>(requestHandlerParams: Params<T, E>) => {
  const { url, onSuccess, onFailure, data, method = 'GET', signal, mediaType, responseType } = requestHandlerParams;
  const currentGeneration = sessionCoordinator.getCurrentGeneration();
  const blockApiCalls = hasPermissionLegacy(PERMISSIONS.BLOCK_API_PERSISTANCE, {
    profile: currentGeneration?.profile ?? null,
  });

  if (blockApiCalls && method !== 'GET') {
    onSuccess(null as T);
    return;
  }

  sendAuthenticatedRequest({
    endpoint: url,
    execute: (requestAccessToken) =>
      apiUtils.request({
        method,
        url,
        data,
        accessToken: requestAccessToken,
        signal: signal ?? null,
        mediaType,
        responseType,
      }),
  })
    .then(get('body'))
    .then(onSuccess)
    .catch((error: any) => {
      if (error.code === 'ABORTED' || error.name === 'AbortError') {
        if (onFailure) {
          onFailure(error);
        }
        return;
      }
      const status = getHttpStatusFromError(error);
      if (status === 500 || status === undefined) {
        console.error('API error', error);
      } else if (isAuthenticationError(error)) {
        console.warn('user unauthorized');
      }
      if (onFailure) {
        onFailure(error);
      }
    });
};

// apiRequestHandler wrapped in a Promise
export const apiRequestHandlerPromise = <T = any>(params: Omit<Params, 'onSuccess' | 'onFailure'>) =>
  new Promise<T>((resolve, reject) => {
    apiRequestHandler({
      onSuccess: (data: T) => resolve(data),
      onFailure: (error) => reject(error),
      ...params,
    });
  });

export default apiRequestHandler;
